import type { RecorderLeadingStep } from '@/sections/asset/components/tabs/credentials/RecorderProgress';
import type { ToolStatus } from '@/sections/asset/components/tabs/credentials/RecorderProgress/buildTimeline';
import type { SubmitStageStatus } from '@/sections/seeds/components/modals/AddSeedByTypeModal/hooks/useAddSeedSubmit';
import { SeedTypeOption } from '@/types';

export const SEED_TYPE_LABEL: Record<SeedTypeOption, string> = {
  [SeedTypeOption.DOMAIN_CIDR]: 'Asset',
  [SeedTypeOption.WEB_APPLICATION]: 'WebApplication',
  [SeedTypeOption.WEB_SERVICE]: 'WebService',
};

export function labelFor(
  status: SubmitStageStatus,
  pendingLabel: string,
  successLabel: string,
  errorLabel: string
): string {
  switch (status) {
    case 'success':
      return successLabel;
    case 'error':
      return errorLabel;
    case 'pending':
    case 'idle':
    case 'skipped':
    default:
      return pendingLabel;
  }
}

// Collapse the 5-state submit lifecycle onto the recorder's 3-state ToolStatus:
// only 'success'/'error' are terminal; everything else (pending/idle/skipped)
// reads as in-progress so the row shows a spinner inside the recorder table.
function toToolStatus(status: SubmitStageStatus): ToolStatus {
  switch (status) {
    case 'success':
      return 'success';
    case 'error':
      return 'error';
    default:
      return 'running';
  }
}

// Builds the two global submit steps as RecorderLeadingStep[] so they can be
// folded INTO each recorder's scrolling table (instead of floating above it).
// Reuses the exact same labelFor + SEED_TYPE_LABEL copy as the standalone
// SubmitProgressStep rows, so the label logic stays single-sourced. The
// credentials step is omitted entirely when credentials are skipped.
export function buildRecorderLeadingSteps(args: {
  seedType: SeedTypeOption | null;
  seedsStatus: SubmitStageStatus;
  credentialsStatus: SubmitStageStatus;
}): RecorderLeadingStep[] {
  const { seedType, seedsStatus, credentialsStatus } = args;
  const typeLabel = seedType ? SEED_TYPE_LABEL[seedType] : 'Seed';

  const steps: RecorderLeadingStep[] = [
    {
      id: 'seeds',
      title: labelFor(
        seedsStatus,
        `Adding ${typeLabel}(s)...`,
        `${typeLabel}(s) added`,
        `Failed to add ${typeLabel}(s)`
      ),
      status: toToolStatus(seedsStatus),
    },
  ];

  if (credentialsStatus !== 'skipped') {
    steps.push({
      id: 'credentials',
      title: labelFor(
        credentialsStatus,
        'Adding Credentials...',
        'Credentials added',
        'Failed to add credentials for some seeds'
      ),
      status: toToolStatus(credentialsStatus),
    });
  }

  return steps;
}
